/**
 * AppWebAppRoutes
 * 앱웹 플랫폼 전용 라우팅 설정
 */
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import HomePageForAdmin from "../features/home/components/HomePageForAdmin";
import LoginPage from "../features/login/components/LoginPage";
import PasswordSetupPage from "../features/login/components/PasswordSetupPage";
import FindAccountPage from "../features/login/components/FindAccountPage";
import HomePageForRider from '../features/home/components/HomePageForRider';
import HomePageForSupplier from "../features/home/components/HomePageForSupplier";
import DeliveryPage from "../features/home/delivery/components/DeliveryPage";
import DeliveryHistoryDetailPage from "../features/home/delivery/components/DeliveryHistoryDetailPage";
import SettlementPage from "../features/settlement/components/SettlementPage";
import WithdrawalPage from "../features/settlement/withdrawal/components/WithdrawalPage";
import TransferPage from "../features/settlement/transfer/components/TransferPage";
import MyPage from "../features/home/mypage/components/MyPage";
import DeductionPage from "../features/deduction/components/DeductionPage";
import DeductionDetailPage from "../features/deduction/components/DeductionDetailPage";
import RegistrationRequestPage from "../features/login/components/RegistrationRequestPage";
import RegistrationCompletePage from "../features/login/components/RegistrationCompletePage";
import SplashScreen from "../features/common/components/SplashScreen";
import MaintenanceScreen from "../features/system/maintenance/components/MaintenanceScreen";
import { useMaintenanceContext } from "../features/system/maintenance/contexts/MaintenanceContext";
import { getUserInfo, getRequiresPasswordSetup } from "../core/utils";
import { getCookie } from "../core/utils/cookie";
import { useAuth } from "../core/contexts/AuthContext";

/**
 * 보호된 라우트 컴포넌트
 * 로그인 여부를 체크하고, 로그인되지 않았지만 requiresPasswordSetup이 true이면 password-setup으로 리다이렉트
 * 점검 중일 때 협력사/라이더에게 점검 화면 표시
 */
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    const userInfo = getUserInfo();
    const requiresPasswordSetup = getRequiresPasswordSetup();
    const accessToken = getCookie('accessToken');
    const { userType } = useAuth();
    const { activeMaintenance, isMaintenance, loading: maintenanceLoading } = useMaintenanceContext();
    
    // 로그인되지 않은 경우 (userInfo에 riderId나 suppId가 없고 accessToken도 없는 경우)
    const isLoggedIn = (userInfo && (userInfo.riderId || userInfo.suppId)) || !!accessToken;
    
    // 로그인이 안 되어 있지만 requiresPasswordSetup이 true이면 비밀번호 설정 페이지로
    if (!isLoggedIn && requiresPasswordSetup) {
        return <Navigate to="/password-setup" replace />;
    }
    
    // 로그인되지 않은 경우
    if (!isLoggedIn) {
        return <Navigate to="/login" replace />;
    }

    // 점검 중이고, 본사(HEADQUARTERS)가 아닌 경우 점검 화면 표시
    // localStorage에서 직접 읽어서 초기 렌더링 시에도 정확하게 판단
    const currentUserType = localStorage.getItem('USER_TYPE') || userType;
    if (!maintenanceLoading && isMaintenance && currentUserType !== 'HEADQUARTERS') {
        return <MaintenanceScreen maintenance={activeMaintenance} />;
    }

    return children;
};

/**
 * 직영 협력사 전용 라우트 컴포넌트
 * 가맹 협력사의 정산/출금/일차감 페이지 접근을 막습니다
 * 점검 중일 때 점검 화면 표시
 */
const DirectSupplierRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    const userInfo = getUserInfo();
    const { suppType, userType, loading } = useAuth();
    const requiresPasswordSetup = getRequiresPasswordSetup();
    const accessToken = getCookie('accessToken');
    const { activeMaintenance, isMaintenance, loading: maintenanceLoading } = useMaintenanceContext();

    // 로딩 중이면 대기
    if (loading) {
        return <SplashScreen />;
    }

    // 로그인되지 않은 경우
    const isLoggedIn = (userInfo && (userInfo.riderId || userInfo.suppId)) || !!accessToken;

    if (!isLoggedIn && requiresPasswordSetup) {
        return <Navigate to="/password-setup" replace />;
    }

    if (!isLoggedIn) {
        return <Navigate to="/login" replace />;
    }

    // 점검 중이고, 본사(HEADQUARTERS)가 아닌 경우 점검 화면 표시
    const currentUserType = localStorage.getItem('USER_TYPE') || userType;
    if (!maintenanceLoading && isMaintenance && currentUserType !== 'HEADQUARTERS') {
        return <MaintenanceScreen maintenance={activeMaintenance} />;
    }

    // 가맹 협력사인 경우 홈으로 리다이렉트 (suppType이 'D'가 아니고 값이 있는 경우)
    if (suppType && suppType !== 'D') {
        return <Navigate to="/home" replace />;
    }

    return children;
};

/**
 * 게스트 전용 라우트 컴포넌트
 * 로그인된 사용자가 접근하면 적절한 홈으로 리다이렉트
 */
const GuestRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    const userInfo = getUserInfo();
    const accessToken = getCookie('accessToken');
    const { userType } = useAuth();

    // 로그인된 경우 (userInfo에 riderId나 suppId가 있거나 accessToken이 있는 경우)
    const isLoggedIn = (userInfo && (userInfo.riderId || userInfo.suppId)) || !!accessToken;

    // 로그인된 경우 적절한 홈으로 리다이렉트
    if (isLoggedIn) {
        // 관리자는 adminHome으로
        if (userType === 'HEADQUARTERS' || userInfo?.prmCode === 'A') {
            return <Navigate to="/adminHome" replace />;
        }
        
        // 협력사는 supplierHome으로
        const isSupplier = userInfo && userInfo.suppId && !userInfo.riderId;
        if (isSupplier || userInfo?.prmCode === 'S') {
            return <Navigate to="/supplierHome" replace />;
        }
        
        // 라이더는 delivery 페이지로
        return <Navigate to="/delivery?tab=summary" replace />;
    }

    return children;
};

/**
 * 홈 페이지 컴포넌트
 * 사용자 타입에 따라 적절한 홈 페이지를 렌더링
 */
const HomePage: React.FC = () => {
    const userInfo = getUserInfo();
    const { userType } = useAuth();

    // 관리자 계정인 경우 - adminHome으로 리다이렉트
    if (userType === 'HEADQUARTERS') {
        return <Navigate to="/adminHome" replace />;
    }

    // 협력사 계정인 경우 (suppId가 있고 riderId가 없는 경우)
    const isSupplier = userInfo && userInfo.suppId && !userInfo.riderId;

    if (isSupplier) {
        return <HomePageForSupplier />;
    }

    return <HomePageForRider />;
};

const AppRoutes: React.FC = () => {
    return (
        <BrowserRouter>
            <Routes>
                <Route path="/splash" element={<SplashScreen />} />
                <Route 
                    path="/login" 
                    element={
                        <GuestRoute>
                            <LoginPage />
                        </GuestRoute>
                    } 
                />
                <Route path="/password-setup" element={<PasswordSetupPage />} />
                <Route path="/find-account" element={<FindAccountPage />} />
                <Route path="/registration-request" element={<RegistrationRequestPage />} />
                <Route path="/register" element={<RegistrationCompletePage />} />
                <Route
                    path="/home"
                    element={<Navigate to="/delivery?tab=summary" replace />}
                />
                <Route
                    path="/adminHome"
                    element={
                        <ProtectedRoute>
                            <HomePageForAdmin />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/supplierHome"
                    element={
                        <ProtectedRoute>
                            <HomePageForSupplier />
                        </ProtectedRoute>
                    }
                />
                <Route 
                    path="/delivery" 
                    element={
                        <ProtectedRoute>
                            <DeliveryPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="/delivery-history" 
                    element={<Navigate to="/delivery?tab=detail" replace />}
                />
                <Route 
                    path="/delivery-history/detail" 
                    element={
                        <ProtectedRoute>
                            <DeliveryHistoryDetailPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="/settlement" 
                    element={
                        <DirectSupplierRoute>
                            <SettlementPage />
                        </DirectSupplierRoute>
                    } 
                />
                <Route 
                    path="/withdrawal" 
                    element={
                        <DirectSupplierRoute>
                            <WithdrawalPage />
                        </DirectSupplierRoute>
                    } 
                />
                <Route
                    path="/transfer"
                    element={
                        <ProtectedRoute>
                            <TransferPage />
                        </ProtectedRoute>
                    }
                />
                <Route 
                    path="/deduction" 
                    element={
                        <DirectSupplierRoute>
                            <DeductionPage />
                        </DirectSupplierRoute>
                    } 
                />
                <Route 
                    path="/deduction/detail" 
                    element={
                        <DirectSupplierRoute>
                            <DeductionDetailPage />
                        </DirectSupplierRoute>
                    } 
                />
                <Route 
                    path="/my-page" 
                    element={
                        <ProtectedRoute>
                            <MyPage />
                        </ProtectedRoute>
                    } 
                />
                <Route path="/" element={<Navigate to="/splash" replace />} />
                <Route path="*" element={<Navigate to="/splash" replace />} />
            </Routes>
        </BrowserRouter>
    );
};

export default AppRoutes;
